<script setup lang="ts">
import { computed } from 'vue';

import { placeholderBackgroundColor } from '@/helpers/colors';
import { useI18n } from '@/references/i18n';
import type { Token } from '@/references/tokens';

import TransactionIcon from '@/components/TransactionIcon.vue';
import UiBlinker from '@/components/ui/UiBlinker.vue';
import UiText from '@/components/ui/UiText.vue';
import UiTitle from '@/components/ui/UiTitle.vue';

interface Props {
  amount: string;
  title: string;
  description?: string;
  iconAlt?: string;
  iconSrc?: string;
  iconFallbackText?: string;
  iconFallbackSrc?: string;
  iconFallbackColor?: string;
  highlighted?: boolean;
  strikethrough?: boolean;
  muted?: boolean;
  bordered?: boolean;
  loading?: boolean;
  failed?: boolean;
  skeleton?: boolean;
  loaderColor?: string;
  notAvailable?: boolean;
  tokens?: [Token, Token];
  progress?: number;
}

const props = defineProps<Props>();

const iconFallbackColor = computed(
  (): string =>
    props.iconFallbackColor ??
    placeholderBackgroundColor(props.iconFallbackText ?? props.title.replace('$', ''))
);

const baseAmountSize = computed(() => {
  if (props.notAvailable) {
    return 'sm';
  }

  return props.bordered ? 'xs' : 'md';
});

const { t } = useI18n();
</script>

<template>
  <div class="transaction-list-item-base">
    <TransactionIcon
      class="transaction-list-item-base__icon"
      :fallback-src="props.iconFallbackSrc"
      :alt="props.iconAlt"
      :src="props.iconSrc"
      :fallback-text="props.iconFallbackText"
      :loading="props.loading"
      :failed="props.failed"
      :loader-color="props.loaderColor"
      :fallback-color="iconFallbackColor"
      :tokens="props.tokens"
      :progress="props.progress"
    />
    <div class="transaction-list-item-base__middle">
      <UiTitle class="transaction-list-item-base__title" :level="2" weight="semibold">
        {{ props.title }}
      </UiTitle>
      <UiText
        v-if="props.description"
        class="transaction-list-item-base__description"
        tag="p"
        size="sm"
        weight="medium"
        muted
      >
        {{ props.description }}
      </UiText>
    </div>
    <div class="transaction-list-item-base__right">
      <UiText
        class="transaction-list-item-base__amount"
        tag="p"
        :size="baseAmountSize"
        :class="{
          'transaction-list-item-base__amount_highlighted':
            props.highlighted && !props.skeleton && !props.muted,
          'transaction-list-item-base__amount_bordered': props.bordered || props.notAvailable
        }"
        :muted="props.muted"
        weight="medium"
        :line-through="props.strikethrough"
      >
        <UiBlinker :blinking="props.skeleton">
          {{ props.amount || t('phrases.notAvailable') }}
        </UiBlinker>
      </UiText>
    </div>
  </div>
</template>

<style scoped>
.transaction-list-item-base {
  display: flex;
  align-items: center;
  padding: 8px;
  color: inherit;
  text-decoration: none;
}

.transaction-list-item-base__icon {
  margin: 0 16px 0 0;
}

.transaction-list-item-base__middle {
  flex-shrink: 1;
  min-width: 0;
  margin: 0 16px 0 0;
}

.transaction-list-item-base__title,
.transaction-list-item-base__description {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.transaction-list-item-base__right {
  max-width: calc(100% - 120px);
  margin: 0 0 0 auto;
}

.transaction-list-item-base__amount {
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.transaction-list-item-base__amount_highlighted {
  color: var(--color-green);
}

.transaction-list-item-base__amount_bordered {
  padding: 0 4px;
  border: 1px solid var(--color-secondary-stroke);
  border-radius: 6px;
}
</style>
